import { BASE_URL } from "../utils/config";
import { getAuthToken } from "../services/authService";
import {
	Timeline,
	LeaderboardUser,
	InnerCircleEligibleCourse,
} from "../types/api";

/**
 * Fetches the leaderboard statistics for the authenticated user.
 * @returns A promise resolving to the Timeline object.
 * @throws {Error} If the request fails.
 */
export async function fetchUserTimeline(): Promise<Timeline> {
	// FIX: The "/api" prefix is removed from this path because
	// the BASE_URL already includes it.
	const url = `${BASE_URL}/users/leaderboard_stats`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("Authentication required to fetch leaderboard stats.");
	}

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch leaderboard stats: ${response.status}`);
		}
		return (await response.json()) as Timeline;
	} catch (error: any) {
		console.error("Error fetching leaderboard stats:", error);
		throw new Error(
			error.message || "An error occurred while fetching leaderboard stats.",
		);
	}
}

/**
 * Fetches the public leaderboard.
 * @returns A promise resolving to an array of LeaderboardUser objects.
 * @throws {Error} If the request fails.
 */
export async function fetchLeaderboard(
	records: number = 20,
): Promise<LeaderboardUser[]> {
	// Append records query param only when provided
	const url = `${BASE_URL}/leaderboard/${
		records ? `?records=${encodeURIComponent(String(records))}` : ""
	}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch leaderboard: ${response.status}`);
		}
		return (await response.json()) as LeaderboardUser[];
	} catch (error: any) {
		console.error("Error fetching leaderboard:", error);
		throw new Error(
			error.message || "An error occurred while fetching the leaderboard.",
		);
	}
}

/**
 * Fetches the course specific leaderboard.
 * @returns A promise resolving to an array of LeaderboardUser objects.
 * @throws {Error} If the request fails.
 *
 * @param course_slug - The slug identifier for the course.
 * @param records - Optional number of records to fetch (default is 20).
 */
export async function fetchCourseLeaderboard(
	course_id: number,
	records: number = 20,
): Promise<LeaderboardUser[]> {
	// Append records query param only when provided
	const url = `${BASE_URL}/leaderboard/${course_id}${
		records ? `?records=${encodeURIComponent(String(records))}` : ""
	}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch leaderboard: ${response.status}`);
		}
		return (await response.json()) as LeaderboardUser[];
	} catch (error: any) {
		console.error("Error fetching leaderboard:", error);
		throw new Error(
			error.message || "An error occurred while fetching the leaderboard.",
		);
	}
}

/**
 * Fetches the user-specific leaderboard.
 * @returns A promise resolving to an array of LeaderboardUser objects.
 * @throws {Error} If the request fails.
 */
export async function fetchUserLeaderboard(): Promise<LeaderboardUser[]> {
	const url = `${BASE_URL}/leaderboard/user`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getAuthToken() || ""}`,
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch User leaderboard: ${response.status}`);
		}
		return (await response.json()) as LeaderboardUser[];
	} catch (error: any) {
		console.error("Error fetching User leaderboard:", error);
		throw new Error(
			error.message || "An error occurred while fetching the User leaderboard.",
		);
	}
}

/**
 * Submits a claim for missing points for a given entry.
 */
export async function submitMissingPointsClaim(
	entryId: number,
	message: string,
): Promise<void> {
	const url = `${BASE_URL}/leaderboard/missing-points`;
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${getAuthToken() || ""}`,
		},
		body: JSON.stringify({ entry_id: entryId, message }),
	});
	if (!response.ok) {
		const text = await response.text();
		throw new Error(
			text || `Failed to submit missing points claim: ${response.status}`,
		);
	}
}

/**
 * Fetches the list of courses that have at least one Inner-Circle-enabled
 * hole. Used by the leaderboard page and Competition Terms to disclaim
 * which courses accrue Inner Circle season points and badges.
 */
export async function fetchInnerCircleEligibleCourses(): Promise<
	InnerCircleEligibleCourse[]
> {
	const url = `${BASE_URL}/leaderboard/inner-circle-courses`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: { "Content-Type": "application/json" },
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch inner circle courses: ${response.status}`);
		}
		return (await response.json()) as InnerCircleEligibleCourse[];
	} catch (error: any) {
		console.error("Error fetching inner circle courses:", error);
		throw new Error(
			error.message || "An error occurred while fetching inner circle courses.",
		);
	}
}
